import React from 'react'
import {
  FacebookShareButton,
  TwitterShareButton,
  LinkedinShareButton,
  RedditShareButton,
  EmailShareButton,
  FacebookIcon,
  LinkedinIcon,
  RedditIcon,
  EmailIcon,
} from 'react-share'
import { Button } from './ui/button'
import { Share2, Copy, Check } from 'lucide-react'
import { useState } from 'react'

interface ShareArticleProps {
  url: string
  title: string
  description?: string
  className?: string
}

const ShareArticle: React.FC<ShareArticleProps> = ({
  url,
  title,
  description,
  className = ''
}) => {
  const [copied, setCopied] = useState(false)

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy link:', err)
    }
  }


  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      <div className="flex items-center gap-2">
        <Share2 className="h-4 w-4" />
        <span className="font-medium">Share this article</span>
      </div>
      
      <div className="flex flex-wrap gap-2">
        {/* Social Media Share Buttons */}
        <FacebookShareButton
          url={url}
          className="transition-transform hover:scale-105"
        >
          <div className="flex items-center gap-2 px-3 py-2 bg-[#1877F2] text-white rounded-md hover:bg-[#166FE5] transition-colors">
            <FacebookIcon size={20} round />
            <span className="text-sm font-medium">Facebook</span>
          </div>
        </FacebookShareButton>

        <TwitterShareButton
          url={url}
          title={title}
          className="transition-transform hover:scale-105"
        >
          <div className="flex items-center gap-2 px-3 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
            <span className="text-sm font-medium">X</span>
          </div>
        </TwitterShareButton>

        <LinkedinShareButton
          url={url}
          title={title}
          summary={description}
          className="transition-transform hover:scale-105"
        >
          <div className="flex items-center gap-2 px-3 py-2 bg-[#0077B5] text-white rounded-md hover:bg-[#006BA1] transition-colors">
            <LinkedinIcon size={20} round />
            <span className="text-sm font-medium">LinkedIn</span>
          </div>
        </LinkedinShareButton>

        <RedditShareButton
          url={url}
          title={title}
          className="transition-transform hover:scale-105"
        >
          <div className="flex items-center gap-2 px-3 py-2 bg-[#FF4500] text-white rounded-md hover:bg-[#E63E00] transition-colors">
            <RedditIcon size={20} round />
            <span className="text-sm font-medium">Reddit</span>
          </div>
        </RedditShareButton>

        <EmailShareButton
          url={url}
          subject={title}
          body={description}
          className="transition-transform hover:scale-105"
        >
          <div className="flex items-center gap-2 px-3 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors">
            <EmailIcon size={20} round />
            <span className="text-sm font-medium">Email</span>
          </div>
        </EmailShareButton>

        {/* Copy Link Button */}
        <Button
          variant="outline"
          size="sm"
          onClick={handleCopyLink}
          className="flex items-center gap-2 transition-transform hover:scale-105"
        >
          {copied ? (
            <>
              <Check className="h-4 w-4" />
              <span>Copied!</span>
            </>
          ) : (
            <>
              <Copy className="h-4 w-4" />
              <span>Copy Link</span>
            </>
          )}
        </Button>
      </div>
    </div>
  )
}

export default ShareArticle
